import {
    Alert,
    Box, Button, Card, CardContent,
    FormControl,
    FormHelperText,
    FormLabel, IconButton,
    Input,
    Radio,
    RadioGroup,
    Sheet, Stack, Textarea,
    Typography
} from "@mui/joy";
import {useState} from "react";
import {Add, CloseRounded, ContentCopy, Delete, Edit, Report} from "@mui/icons-material";

import PartModal from "./PartModal";
import {StlViewer} from "react-stl-viewer";
import {PROCESSES} from "../PROCESSES";

export default function App() {
    const [partModal, setPartModal] = useState(false);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [affiliation, setAffiliation] = useState('');
    const [department, setDepartment] = useState('BME');
    const [purpose, setPurpose] = useState('');
    const [course, setCourse] = useState('');
    const [courseAffiliation, setCourseAffiliation] = useState('');
    const [advisorName, setAdvisorName] = useState('');
    const [jobDescription, setJobDescription] = useState('');
    const [parts, setParts] = useState([]);

    const [alert, setAlert] = useState(null);
    const [basisPart, setBasisPart] = useState({});
    const [editedPart, setEditedPart] = useState();

    const addPart = () => {
        setBasisPart({});
        setEditedPart(null);
        setPartModal(true);
    };

    const editPart = (index) => {
        setBasisPart(parts[index]);
        setEditedPart(index);
        setPartModal(true);
    };

    const duplicatePart = (index) => {
        setBasisPart(parts[index]);
        setEditedPart(null);
        setPartModal(true);
    };

    const deletePart = (index) => {
        setParts((oldParts) => [...oldParts.slice(0, index), ...oldParts.slice(index + 1)]);
    };

    const submitJob = () => {
        const simpleTextVerify = (text) => text === null || text === undefined || text.length === 0;

        if (simpleTextVerify(name)) {
            setAlert({
                title: 'Error',
                body: 'Make sure your name is entered correctly.',
            })
            return;
        }

        if (simpleTextVerify(email) || !email.endsWith('@gatech.edu')) {
            setAlert({
                title: 'Error',
                body: 'Make sure your email is entered correctly. Only Georgia Tech emails are accepted.',
            })
            return;
        }

        if (simpleTextVerify(affiliation)) {
            setAlert({
                title: 'Error',
                body: 'Affiliation must be specified.',
            })
            return;
        }

        if (simpleTextVerify(department)) {
            setAlert({
                title: 'Error',
                body: 'Department must be specified.',
            })
            return;
        }

        if (simpleTextVerify(purpose)) {
            setAlert({
                title: 'Error',
                body: 'Purpose must be specified.',
            })
            return;
        }

        if (purpose === 'course' && simpleTextVerify(course)) {
            setAlert({
                title: 'Error',
                body: 'Course must be specified.',
            })
            return;
        }

        if (purpose === 'course' && simpleTextVerify(courseAffiliation)) {
            setAlert({
                title: 'Error',
                body: 'Course affiliation must be specified.',
            })
            return;
        }

        if (simpleTextVerify(advisorName)) {
            setAlert({
                title: 'Error',
                body: 'Advisor name must be provided.',
            })
            return;
        }

        if (simpleTextVerify(jobDescription)) {
            setAlert({
                title: 'Error',
                body: 'Job description must be provided.',
            })
            return;
        }

        if (parts.length === 0) {
            setAlert({
                title: 'No parts!',
                body: 'Your job cannot be submitted without any parts.',
            })
            return;
        }


    };

    return (
        <>
            {partModal &&
                <PartModal
                    onClose={() => setPartModal(false)}
                    partSubmit={(partData) => {
                        if (editedPart !== undefined && editedPart !== null) {
                            setPartModal(false);
                            setParts((oldParts) => [...oldParts.slice(0, editedPart), partData, ...oldParts.slice(editedPart + 1)]);
                        } else {
                            setPartModal(false);
                            setParts((oldParts) => [...oldParts, partData]);
                        }
                    }}
                    basis={basisPart}
                    editing={Boolean(editedPart)}
                    open
                />
            }

            <Sheet
                sx={{
                    maxWidth: '700px',
                    m: '100px auto',
                    background: 'none',
                }}
            >
                <Typography
                    element="h2"
                    fontSize="30px"
                    fontFamily="'Inter', sans-serif"
                    fontWeight="500"
                    mb="70px"
                >
                    BME Job Request
                </Typography>

                <Typography
                    element="h1"
                    fontSize="20px"
                    fontFamily="'Inter', sans-serif"
                    fontWeight="500"
                    textAlign="center"
                >
                    Identity
                </Typography>

                <FormControl sx={{ mt: '25px' }}>
                    <FormLabel>
                        Name
                        <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <Input
                        placeholder="George P. Burdell"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </FormControl>

                <FormControl sx={{ mt: '25px' }}>
                    <FormLabel>
                        Email
                        <span style={{color: 'red'}}>*</span>
                    </FormLabel>
                    <Input
                        placeholder="gburdell@gatech.edu"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <FormHelperText>
                        Your Georgia Tech email. This email will be used for progress updates and as a point
                        of contact if an issue with your job arises.
                    </FormHelperText>
                </FormControl>

                <FormControl sx={{ mt: '35px' }}>
                    <FormLabel>
                        Affiliation
                        <span style={{color: 'red'}}>*</span>
                    </FormLabel>
                    <RadioGroup
                        defaultValue="undergraduate"
                        name="controlled-radio-buttons-group"
                        value={affiliation}
                        onChange={(e) => setAffiliation(e.target.value)}
                        sx={{ my: 1 }}
                    >
                        <Radio value="undergraduate" label="Undergraduate Student" />
                        <Radio value="graduate" label="Graduate Student" />
                        <Radio value="doctoral" label="PhD Student/Candidate" />
                        <Radio value="research" label="Research Faculty/Staff" />
                        <Radio value="faculty" label="Professor/Faculty" />
                        <Radio value="other" label="Affiliate/Other" />
                    </RadioGroup>
                </FormControl>

                <FormControl sx={{ mt: '25px' }}>
                    <FormLabel>
                        Department
                        <span style={{color: 'red'}}>*</span>
                    </FormLabel>
                    <Input
                        placeholder="NRE"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                    />
                    <FormHelperText>
                        Your home department/program (or department for which the job is being made). Change if not BME.
                    </FormHelperText>
                </FormControl>

                <Typography
                    element="h2"
                    fontSize="20px"
                    fontFamily="'Inter', sans-serif"
                    fontWeight="500"
                    textAlign="center"
                    mt="40px"
                >
                    General Information
                </Typography>

                <FormControl sx={{ mt: '35px' }}>
                    <FormLabel>
                        Purpose
                        <span style={{color: 'red'}}>*</span>
                    </FormLabel>
                    <RadioGroup
                        name="controlled-radio-buttons-group"
                        value={purpose}
                        onChange={(e) => setPurpose(e.target.value)}
                        sx={{ my: 1 }}
                    >
                        <Radio value="course" label="Class" />
                        <Radio value="research" label="Research" />
                        <Radio value="organization" label="Organization" />
                        <Radio value="competition" label="Create-X/Competition" />
                    </RadioGroup>
                </FormControl>

                {purpose === 'course' &&
                    <>
                        <FormControl sx={{ mt: '35px' }}>
                            <FormLabel>
                                Course/Class
                                <span style={{color: 'red'}}>*</span>
                            </FormLabel>
                            <RadioGroup
                                name="controlled-radio-buttons-group"
                                value={course}
                                onChange={(e) => setCourse(e.target.value)}
                                sx={{ my: 1 }}
                            >
                                <Radio value="Capstone" label="BME Capstone" />
                                <Radio value="BMED 2310" label="BMED 2310" />
                                <Radio value="" label="Other" checked={course !== undefined && !['Capstone', 'BMED 2310'].includes(course)} />
                            </RadioGroup>
                        </FormControl>

                        {(course !== undefined && !['Capstone', 'BMED 2310'].includes(course)) &&
                            <FormControl sx={{ mt: '20px' }}>
                                <FormLabel>
                                    Course Name
                                    <span style={{color: 'red'}}>*</span>
                                </FormLabel>
                                <Input
                                    placeholder="BMED 2310"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </FormControl>
                        }

                        <FormControl sx={{ mt: '35px' }}>
                            <FormLabel>
                                Class Affiliation
                                <span style={{color: 'red'}}>*</span>
                            </FormLabel>
                            <RadioGroup
                                name="controlled-radio-buttons-group"
                                value={courseAffiliation}
                                onChange={(e) => setCourseAffiliation(e.target.value)}
                                sx={{ my: 1 }}
                            >
                                <Radio value="student" label="Student" />
                                <Radio value="ta" label="TA" />
                                <Radio value="professor" label="Professor/Lecturer" />
                            </RadioGroup>
                        </FormControl>
                    </>
                }

                {courseAffiliation !== 'professor' &&
                    <FormControl sx={{ mt: '30px' }}>
                        <FormLabel>
                            First and Last Name of Professor/PI/Advisor
                            <span style={{color: 'red'}}>*</span>
                        </FormLabel>
                        <Input
                            placeholder="Dr. George P. Burdell"
                            value={advisorName}
                            onChange={(e) => setAdvisorName(e.target.value)}
                        />
                    </FormControl>
                }

                <Typography
                    element="h2"
                    fontSize="20px"
                    fontFamily="'Inter', sans-serif"
                    fontWeight="500"
                    textAlign="center"
                    mt="50px"
                    mb="20px"
                >
                    Job Details
                </Typography>

                <FormControl>
                    <FormLabel>Job/Project Description</FormLabel>
                    <Textarea
                        placeholder="Making a sign for the GT Robotics Club."
                        minRows={3}
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                    />
                    <FormHelperText>
                        Provide a brief description about the job and/or project (that is, what you are making or
                        trying to make). We use this information to gauge tolerances, determine settings, and provide
                        additional help or recommendations.
                    </FormHelperText>
                </FormControl>

                <Typography
                    element="h3"
                    fontSize="16px"
                    fontFamily="'Inter', sans-serif"
                    fontWeight="500"
                    textAlign="center"
                    mt="50px"
                    mb="20px"
                >
                    Parts
                </Typography>

                <Sheet color="neutral">
                    {parts.map((part, index) => (
                        <Card
                            key={part.modelName + index}
                            variant="outlined"
                            sx={{
                                mb: '10px',
                                p: '5px',
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between">
                                    <Stack direction="row">
                                        {part.modelName.toLowerCase().endsWith('.stl') ?
                                            <StlViewer
                                                shadows
                                                style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    border: 'solid 1px #ddd',
                                                    borderRadius: '7px',
                                                    backgroundColor: '#fff',
                                                }}
                                                cameraProps={{
                                                    initialPosition: {
                                                        distance: 1.7,
                                                    }
                                                }}
                                                url={part.url}
                                            /> :
                                            <img
                                                style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    border: 'solid 1px #ddd',
                                                    borderRadius: '7px',
                                                    backgroundColor: '#fff',
                                                }}
                                                alt=""
                                                src={part.url}
                                            />
                                        }

                                        <Stack sx={{ ml: '10px' }}>
                                            <Typography level="title-md" textColor="#333">
                                                {PROCESSES[part.jobType].name}
                                            </Typography>
                                            <Typography textColor="#333" fontSize="12px">
                                                {part.modelName} ({Math.ceil(part.file.size / 1000)} KB)
                                            </Typography>
                                            <Typography textColor="#333" fontSize="12px">
                                                {PROCESSES[part.jobType].materials[part.material].name} {part.materialVersion && ` | ${PROCESSES[part.jobType].materials[part.material].versions[part.materialVersion].name}`}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack direction="row" alignContent="top">
                                        <IconButton onClick={() => editPart(index)}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton onClick={() => duplicatePart(index)}>
                                            <ContentCopy />
                                        </IconButton>
                                        <IconButton color="danger" onClick={() => deletePart(index)}>
                                            <Delete />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    <Card variant="solid" sx={{ backgroundColor: '#eee', cursor: 'pointer' }} onClick={addPart}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Add sx={{ color: '#333' }} />
                                <Typography level="title-md" textColor="#333" ml="5px">
                                    Add Part
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Sheet>

                {alert &&
                    <Alert
                        startDecorator={<Report />}
                        variant="soft"
                        color="danger"
                        endDecorator={
                            <IconButton variant="soft" color="danger">
                                <CloseRounded />
                            </IconButton>
                        }
                        sx={{
                            alignItems: 'flex-start',
                            mt: '40px'
                        }}
                    >
                        <div>
                            <div>{alert.title}</div>
                            <Typography level="body-sm" color="danger">
                                {alert.body}
                            </Typography>
                        </div>
                    </Alert>
                }

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: '70px'
                    }}
                >
                    <Button onClick={submitJob} loadingPosition="start" loading>
                        Uploading files...
                    </Button>
                </Box>
            </Sheet>
        </>
    );

}